import React, { useEffect, useId, useRef, useState } from "react";
import { Icon, Input, Text } from "@zorni/components-lib";

import styles from "./AutocompleteInput.module.css";

/**
 * Champ texte avec suggestions fournies par le parent (API). Contrairement à
 * SearchSelect, la liste n'affiche QUE les `options` reçues : rien n'est
 * refiltré côté client (Photon trouve « saint etienne » pour « Saint-Étienne »)
 * et le texte tapé n'est jamais ajouté comme option. Le champ affiche
 * toujours `value`, sans mécanisme de resynchronisation sur un libellé.
 *
 * options : [{ key, label }] — `key` unique par suggestion.
 * onChange(text) : à chaque frappe. onSelect(option) : suggestion choisie.
 * helperText est rendu sous le champ, hors de la zone d'ancrage de la liste,
 * pour que celle-ci reste collée au champ.
 */
const AutocompleteInput = ({
  name,
  label,
  value,
  onChange,
  options = [],
  onSelect,
  isLoading = false,
  showEmpty = false,
  loadingText = "Chargement…",
  emptyText = "Aucun résultat",
  disabled = false,
  helperText,
  errors,
  iconName = "iconLocation",
}) => {
  const listboxId = `${useId()}-listbox`;
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    setHighlight(-1);
  }, [options]);

  const hasOptions = options.length > 0;
  const showPopover = open && !disabled && (isLoading || hasOptions || showEmpty);

  const select = (option) => {
    setOpen(false);
    setHighlight(-1);
    onSelect?.(option);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!hasOptions) return;
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => {
        if (event.key === "ArrowDown") return Math.min(current + 1, options.length - 1);
        return Math.max(current - 1, 0);
      });
    } else if (event.key === "Enter") {
      if (showPopover && highlight >= 0 && options[highlight]) {
        event.preventDefault();
        select(options[highlight]);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    }
  };

  const handleBlur = (event) => {
    if (!wrapperRef.current?.contains(event.relatedTarget)) setOpen(false);
  };

  return (
    <div className={styles.root}>
      <div ref={wrapperRef} className={styles.anchor} onBlur={handleBlur}>
        <Input
          floatingLabel
          inputSize="medium"
          label={label}
          name={name}
          value={value}
          disabled={disabled}
          errors={errors}
          autoComplete="off"
          onChange={(event) => {
            setOpen(true);
            onChange?.(event.target.value);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={showPopover}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={highlight >= 0 ? `${listboxId}-opt-${highlight}` : undefined}
        />

        {showPopover && (
          <div className={styles.popover}>
            <ul id={listboxId} role="listbox" className={styles.list}>
              {isLoading && (
                <li role="presentation" className={`${styles.row} ${styles.status}`}>
                  <Text size="sm" color="subtle">
                    {loadingText}
                  </Text>
                </li>
              )}
              {!isLoading &&
                options.map((option, index) => (
                  <li
                    key={option.key}
                    id={`${listboxId}-opt-${index}`}
                    role="option"
                    aria-selected={index === highlight}
                    className={`${styles.row} ${index === highlight ? styles.active : ""}`}
                    onMouseEnter={() => setHighlight(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      select(option);
                    }}
                  >
                    <span className={styles.iconBox}>
                      <Icon name={iconName} size="md" />
                    </span>
                    <Text size="sm" className={styles.optionLabel}>
                      {option.label}
                    </Text>
                  </li>
                ))}
              {!isLoading && !hasOptions && showEmpty && (
                <li role="presentation" className={`${styles.row} ${styles.status}`}>
                  <Text size="sm" color="subtle">
                    {emptyText}
                  </Text>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>

      {helperText && (
        <Text as="div" size="sm" color="subtle" className={styles.helper}>
          {helperText}
        </Text>
      )}
    </div>
  );
};

export default AutocompleteInput;
